import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Users, BedDouble, Bath, MapPin, ArrowUpRight, Star, ChevronLeft, ChevronRight } from "lucide-react";
import { useLang } from "@/context/LanguageContext";

export const ApartmentCard = ({ apt, index = 0 }) => {
  const { t } = useLang();
  const photos = apt.photos || [];
  const [photoIdx, setPhotoIdx] = useState(0);

  const nav = (e, dir) => {
    e.preventDefault();
    e.stopPropagation();
    setPhotoIdx((p) => (p + dir + photos.length) % photos.length);
  };
  const goTo = (e, i) => {
    e.preventDefault();
    e.stopPropagation();
    setPhotoIdx(i);
  };

  return (
    <Link
      to={`/apartments/${apt.id}`}
      data-testid={`apartment-card-${apt.id}`}
      className="group block bg-card border border-line rounded-sm overflow-hidden transition-all duration-500 hover:border-petrol/30 hover:-translate-y-1"
      style={{ animationDelay: `${index * 0.08}s` }}
    >
      <div className="relative aspect-[4/3] overflow-hidden bg-beige" data-testid={`card-gallery-${apt.id}`}>
        <img
          src={photos[photoIdx]}
          alt={apt.name}
          loading="lazy"
          className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
        />
        {photos.length > 1 && (
          <>
            <button
              type="button"
              onClick={(e) => nav(e, -1)}
              data-testid={`card-prev-${apt.id}`}
              aria-label="Foto precedente"
              className="absolute left-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-cream/85 backdrop-blur flex items-center justify-center text-petrol shadow-sm opacity-0 group-hover:opacity-100 transition-opacity hover:bg-cream"
            >
              <ChevronLeft size={18} strokeWidth={1.5} />
            </button>
            <button
              type="button"
              onClick={(e) => nav(e, 1)}
              data-testid={`card-next-${apt.id}`}
              aria-label="Foto successiva"
              className="absolute right-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-cream/85 backdrop-blur flex items-center justify-center text-petrol shadow-sm opacity-0 group-hover:opacity-100 transition-opacity hover:bg-cream"
            >
              <ChevronRight size={18} strokeWidth={1.5} />
            </button>
            <div className="absolute bottom-3 left-0 right-0 flex justify-center gap-1.5">
              {photos.map((_, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={(e) => goTo(e, i)}
                  data-testid={`card-dot-${apt.id}-${i}`}
                  aria-label={`Vai alla foto ${i + 1}`}
                  className={`h-1.5 rounded-full transition-all ${i === photoIdx ? "w-5 bg-cream" : "w-1.5 bg-cream/60 hover:bg-cream/90"}`}
                />
              ))}
            </div>
          </>
        )}
      </div>
      <div className="p-6">
        <div className="flex items-center gap-1.5 text-muted-foreground text-xs mb-2">
          <MapPin size={13} strokeWidth={1.5} />
          <span>{apt.location}</span>
        </div>
        <div className="flex items-start justify-between gap-2">
          <h3 className="font-serif text-2xl text-petrol leading-tight">{apt.name}</h3>
          <ArrowUpRight
            size={20}
            strokeWidth={1.5}
            className="text-petrol/40 group-hover:text-gold transition-colors shrink-0 mt-1"
          />
        </div>
        {apt.rating_count > 0 && (
          <div className="flex items-center gap-1.5 mt-2" data-testid={`card-rating-${apt.id}`}>
            <Star size={14} className="fill-gold text-gold" />
            <span className="text-sm font-medium text-petrol">{apt.rating_avg}</span>
            <span className="text-xs text-muted-foreground">({apt.rating_count})</span>
          </div>
        )}
        <div className="flex items-center gap-5 mt-4 text-sm text-muted-foreground">
          <span className="flex items-center gap-1.5">
            <Users size={15} strokeWidth={1.5} /> {apt.max_guests}
          </span>
          <span className="flex items-center gap-1.5">
            <BedDouble size={15} strokeWidth={1.5} /> {apt.bedrooms}
          </span>
          <span className="flex items-center gap-1.5">
            <Bath size={15} strokeWidth={1.5} /> {apt.bathrooms}
          </span>
        </div>
        <div className="mt-5 pt-5 border-t border-line flex items-center justify-between">
          <span className="text-sm text-petrol link-underline">{t("apartments.details")}</span>
          <span className="text-xs text-gold overline">{t("apartments.check_availability")}</span>
        </div>
      </div>
    </Link>
  );
};

export default ApartmentCard;
